import { useEffect } from 'react';

import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteError,
  isRouteErrorResponse,
  useMatches,
  useLoaderData,
} from '@remix-run/react';
import type { LinksFunction, MetaFunction, LoaderFunctionArgs } from '@remix-run/node';

import { authenticator } from '~/services/auth.server';
import { initPostHog } from '~/lib/posthog.client';
import { getAppTitle } from '~/lib/meta';
import { getInstanceBadge } from '~/lib/instance';
import { parseComposerHeight } from '~/utils/composerHeightState';
import { Toaster as SonnerToaster } from '~/components/ui/sonner';
// @ts-expect-error - trips up on the ?url loader option
import styles from './tailwind.css?url';
// @ts-expect-error - Vite ?url import for font CSS
import fontNationalStyles from './font-National.css?url';
// @ts-expect-error - Vite ?url import for sonner CSS
import sonnerStyles from 'sonner/dist/styles.css?url';
// Loaded here (not from the client-only map module) so the stylesheet is in <head>
// before the map mounts — otherwise the dev server injects it late and Leaflet tiles
// render unsized/scattered.
import leafletStyles from 'leaflet/dist/leaflet.css?url';
import { VersionUpdateBanner } from '~/components/VersionUpdateBanner';
import { env } from '~/env.server';

// Smarty's embedded key authenticates via the Referer header on its allowlist,
// so the US Street verify call has to run in the browser. Exposing just this
// one key matches the precedent set by GOOGLE_MAPS_API_KEY in
// app/utils/googlemaps.tsx — no other server-side secrets should pass through
// this loader.
export const loader = async ({ request }: LoaderFunctionArgs) => {
  const user = await authenticator.isAuthenticated(request);
  return {
    publicEnv: {
      SMARTY_EMBEDDED_KEY: env.SMARTY_EMBEDDED_KEY ?? '',
      POSTHOG_HHOS_CAPTURE_KEY: env.POSTHOG_HHOS_CAPTURE_KEY ?? '',
    },
    userEmail: user?.email ?? null,
    // Person UUID — set as a PostHog person property so staff product-analytics
    // events can be joined to the Postgres agent tables (Ticket.currentAssigneeId,
    // Message.senderId, …) by id rather than by email.
    userId: user?.id ?? null,
    // The messaging composer's dragged height. Read here rather than from
    // storage after mount so the server renders the composer at the size the
    // operator left it — see `~/utils/composerHeightState`.
    composerHeightPx: parseComposerHeight(request.headers.get('Cookie')),
  };
};

export type PublicEnv = {
  SMARTY_EMBEDDED_KEY: string;
  POSTHOG_HHOS_CAPTURE_KEY: string;
};

export const meta: MetaFunction = () => {
  return [{ title: getAppTitle() }, { name: 'description', content: 'Honey Homes OS' }];
};

export const links: LinksFunction = () => [
  { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
  {
    rel: 'preconnect',
    href: 'https://fonts.gstatic.com',
    crossOrigin: 'anonymous',
  },
  // Home photos, avatars, and attachments are served from Google Cloud Storage;
  // warm the connection so the first image isn't blocked on TCP+TLS setup. No
  // crossOrigin — plain <img> fetches are non-CORS, so a CORS preconnect wouldn't
  // be reused. dns-prefetch is a fallback for browsers that ignore preconnect.
  { rel: 'preconnect', href: 'https://storage.googleapis.com' },
  { rel: 'preconnect', href: 'https://firebasestorage.googleapis.com' },
  { rel: 'dns-prefetch', href: 'https://storage.googleapis.com' },
  { rel: 'dns-prefetch', href: 'https://firebasestorage.googleapis.com' },
  // Preload the two most-used National2 weights so text isn't held back by late
  // font discovery on cold loads. crossOrigin is required even same-origin —
  // fonts are always fetched in CORS mode, so without it the preload is wasted.
  {
    rel: 'preload',
    as: 'font',
    type: 'font/woff2',
    href: '/assets/fonts/National2-Regular.woff2',
    crossOrigin: 'anonymous',
  },
  {
    rel: 'preload',
    as: 'font',
    type: 'font/woff2',
    href: '/assets/fonts/National2-Medium.woff2',
    crossOrigin: 'anonymous',
  },
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap',
  },
  { rel: 'stylesheet', href: fontNationalStyles },
  { rel: 'stylesheet', href: styles },
  { rel: 'stylesheet', href: sonnerStyles },
  { rel: 'stylesheet', href: leafletStyles },
  {
    rel: 'apple-touch-icon',
    sizes: '180x180',
    href: '/assets/images/apple-touch-icon.png',
  },
  {
    rel: 'manifest',
    href: '/site.webmanifest',
  },
];

function Favicon() {
  const matches = useMatches();
  const emoji = matches.reduce<string | null>(
    (acc, match) => (match.handle as { emoji?: string })?.emoji || acc,
    null
  );

  const badge = getInstanceBadge();
  const border = badge
    ? `<rect x='4' y='4' width='92' height='92' rx='14' fill='none' stroke='${badge.borderHexEncoded}' stroke-width='8'/>`
    : '';

  const href = emoji
    ? `data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><rect width='100' height='100' rx='18' fill='%230d9488'/><text x='50' y='50' text-anchor='middle' dominant-baseline='central' font-size='62'>${emoji}</text>${border}</svg>`
    : badge
      ? badge.faviconHref
      : '/assets/images/favicon.svg';

  return <link rel="icon" type="image/svg+xml" href={href} />;
}

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, viewport-fit=cover, interactive-widget=resizes-content"
        />
        {/* Let an "Add to Home Screen" bookmark launch full-screen like an app
         * and respect the notch / safe areas on iOS Safari. */}
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="apple-mobile-web-app-title" content="hhOS" />
        {/*Indicated at https://fullcalendar.io/docs/react*/}
        <style data-fullcalendar />
        <Meta />
        <Links />
        <Favicon />
      </head>
      <body id="root">
        <div>{children}</div>
        <SonnerToaster />
        <VersionUpdateBanner />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export const ErrorBoundary = () => {
  const error = useRouteError();
  if (error?.status !== 404) {
    console.error(error);
  }
  return (
    <div>
      <h1>
        {isRouteErrorResponse(error)
          ? `${error.status} ${error.statusText}`
          : error instanceof Error
            ? error.message
            : 'Unknown Error'}
      </h1>
      <pre>{JSON.stringify(error)}</pre>
    </div>
  );
};

export default function App() {
  const { publicEnv, userEmail, userId } = useLoaderData<typeof loader>();
  // Boot config is published to `window` by the SSR'd markup, so initPostHog
  // reads it without reactive deps. `<` is escaped to keep the serialized JSON
  // from being able to close the <script> tag.
  const posthogBoot = JSON.stringify({
    key: publicEnv.POSTHOG_HHOS_CAPTURE_KEY,
    email: userEmail,
    personId: userId,
  }).replace(/</g, '\\u003c');

  // Must stay inside an effect so PostHog boots only after the hydration
  // commit — see initPostHog's docs for why (React #418, HHOS-1892).
  useEffect(() => {
    initPostHog();
  }, []);

  return (
    <>
      <script dangerouslySetInnerHTML={{ __html: `window.__hhPosthog=${posthogBoot}` }} />
      <Outlet />
    </>
  );
}

export function TailwindIndicator() {
  if (process.env.NODE_ENV === 'production') return null;

  return (
    <div className="fixed bottom-1 left-1 z-50 flex h-6 w-6 items-center justify-center rounded-full bg-gray-800 p-3 font-mono text-xs text-white">
      <div className="block sm:hidden">xs</div>
      <div className="hidden sm:block md:hidden">sm</div>
      <div className="hidden md:block lg:hidden">md</div>
      <div className="hidden lg:block xl:hidden">lg</div>
      <div className="hidden xl:block 2xl:hidden">xl</div>
      <div className="hidden 2xl:block">2xl</div>
    </div>
  );
}
